import { FC, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { PopoverProps, Typography } from '@mui/material';
import { PeopleAltOutlined, DeleteOutline, MoreHoriz, PaletteOutlined } from '@mui/icons-material';

import { useModalContext } from '../../modal/ModalContext';
import { useApiMutation } from '../../query/hooks';
import { CustomPopover } from '../../ui/CustomPopover';
import { ColorPicker } from '../../components/ColorPicker';
import { graphql } from '../../gql';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { Journey_SingleJourneyFragmentFragment } from '../../gql/graphql';
import { useSnackbar } from '../../store/snackbar';

const journeyPopover_updateJourneyColorMutation = graphql(`
	mutation journey_updateColor($id: UUID!, $color: String!) {
		updateJourney(by: { id: $id }, data: { color: $color }) {
			ok
			errorMessage
		}
	}
`);

type Props = {
	journeyData: Journey_SingleJourneyFragmentFragment;
};

export const JourneyPopover: FC<Props> = ({ journeyData }) => {
	const theme = useTheme();
	const intl = useIntl();
	const { setModal } = useModalContext();
	const [colorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
	const [colorPopoverAnchorEl, setColorPopoverAnchorEl] = useState<PopoverProps['anchorEl']>(null);
	const { mutate: updateJourneyColor } = useApiMutation(journeyPopover_updateJourneyColorMutation);
	const ref = useRef<HTMLLIElement>(null);
	const forceCloseCustomPopoverEvent = useRef<Event>(new Event('forceClosePopover-color-change'));
	const { openSnackbar } = useSnackbar();

	return (
		<>
			<CustomPopover
				layoutIcon={<MoreHoriz />}
				forceCloseEvent={forceCloseCustomPopoverEvent.current}
				closeOnAction={false}
				options={[
					{
						icon: <PaletteOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
						caption: <FormattedMessage id={'journey.popover.set-color'} />,
						ref: ref,
						onAction: () => {
							const getBoundingClientRect = () => {
								return ref.current!.getBoundingClientRect();
							};

							setColorPopoverOpen((prevState) => !prevState);
							setColorPopoverAnchorEl({ getBoundingClientRect, nodeType: 1 });
						},
					},
					{
						icon: <PeopleAltOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
						caption: <FormattedMessage id={'journey.popover.set-teams'} />,
						onAction: () => setModal({ type: 'editJourney', payload: { id: journeyData.id, name: journeyData.name }, maxWidth: 'xs' }),
					},
					{
						icon: <DeleteOutline sx={{ fontSize: 16, fill: theme.palette.error.main }} />,
						caption: (
							<Typography color={theme.palette.error.main}>
								<FormattedMessage id={'journey.popover.delete'} />
							</Typography>
						),
						onAction: () => setModal({ type: 'deleteJourney', payload: { id: journeyData.id, name: journeyData.name } }),
					},
				]}
			/>
			<ColorPicker
				open={colorPopoverOpen}
				defaultValue={journeyData.color}
				anchorEl={colorPopoverAnchorEl}
				onClose={() => setColorPopoverOpen(false)}
				onChange={(event) => {
					updateJourneyColor(
						{ id: journeyData.id, color: event.target.value },
						{
							onSuccess: (result) =>
								mutationResponseHandler({
									result: result.updateJourney.ok,
									toastMessages: {
										error: intl.formatMessage({ id: 'common.error.default' }),
									},
									errorMessage: result.updateJourney.errorMessage,
									callback: () => {
										setColorPopoverOpen(false);
										window.dispatchEvent(forceCloseCustomPopoverEvent.current);
									},
									openSnackbar,
								}),
						},
					);
				}}
			/>
		</>
	);
};
